import { UserHeader } from 'components/headers';
import { TrendValue } from 'components/text';
import { AutoshipsColumn } from 'features/dashboard/widgets/firstline/columns/AutoshipsColumns';
import { CheckAssuranceColumn } from 'features/dashboard/widgets/firstline/columns/CheckAssuranceColumn';
import { FitlineClubMemberColumn } from 'features/dashboard/widgets/firstline/columns/FitlineClubMemberColumn';
import {
  CurrentQualificationColumn,
  HighesthQualificationColumn,
  PreviousMonthQualificationColumn
} from 'features/dashboard/widgets/firstline/columns/QualificationColumns';
import {
  OpenAutoshipVolumeColumn,
  PersonalSalesColumn,
  SixLevelSalesColumn,
  StructureSalesColumn
} from 'features/dashboard/widgets/firstline/columns/SalesColumns';
import { Icon, IconName } from 'libs/icons';
import { Month } from 'libs/time';
import { useTranslation } from 'libs/translations';
import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import {
  MdOutlineSubdirectoryArrowRight,
  MdOutlineHistory
} from 'react-icons/md';
import { NavLink } from 'react-router-dom';
import { IUserProfile } from 'types';
import { computePercentageTrend } from 'utils';

import { useDownlineContext } from '../DownlineContext';
import { HistoryTable } from '../history-table/HistoryTable';

interface ITeamTableRowProps {
  readonly containerWidth: number;
  readonly user: IUserProfile;
  readonly type?: 'upline' | 'downline' | 'selectedUser';
  readonly onExploreDownline: (userId: string) => void;
}

export const TeamTableRow = ({
  containerWidth,
  user,
  type = 'downline',
  onExploreDownline
}: ITeamTableRowProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExploreDownlineClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onExploreDownline(user.userId);
    },
    [onExploreDownline, user.userId]
  );
  const { selectedPeriod } = useDownlineContext();
  const isCurrentMonth = selectedPeriod.getTime() == Month.today().getTime();

  const hasDownline = user.hasDownline && _.isUndefined(user.countryUserId);

  const { sixLevelTrend, structureTrend } = useMemo(() => {
    if (_.isUndefined(user.businessDataSummary)) {
      return { sixLevelTrend: 0, structureTrend: 0 };
    }

    return {
      sixLevelTrend: computePercentageTrend(
        user.businessDataSummary.sixLevelVolume.current,
        user.businessDataSummary.sixLevelVolume.lastMonth
      ),
      structureTrend: computePercentageTrend(
        user.businessDataSummary.structureVolume.current,
        user.businessDataSummary.structureVolume.lastMonth
      )
    };
  }, [user.businessDataSummary]);

  return (
    <>
      <tr
        className={`transition duration-300 ease-in-out  ${
          type === 'selectedUser'
            ? 'bg-secondary'
            : type === 'upline'
            ? 'bg-body'
            : `bg-white border-b-2 border-body`
        }`}
      >
        <td className={`${type == 'downline' ? 'bg-body' : ''}`}>
          <button
            disabled={type === 'selectedUser' || !hasDownline}
            title={t('Explore Downline')}
            onClick={handleExploreDownlineClick}
            className="explore-downline-button flex justify-between items-center p-3"
            aria-label={t("Explore {{userName}}'s Downline", {
              userName: user.name
            })}
          >
            {type !== 'downline' ? (
              <MdOutlineSubdirectoryArrowRight size={16} />
            ) : hasDownline ? (
              <Icon name={IconName.PlusSign} size={16} />
            ) : null}
          </button>
        </td>
        <td
          className={`px-2 ${
            type === 'upline' && !isExpanded
              ? 'relative after:w-[2px] after:h-2 after:bg-silver after:absolute after:bottom-[-5px] after:left-[22px]'
              : ''
          }`}
        >
          <button
            onClick={() => setIsExpanded(prev => !prev)}
            className="flex justify-between items-center gap-2 w-full"
          >
            <UserHeader
              user={user}
              showQualification={false}
              showCheckAssurance={false}
              showBadges={false}
              showCountryFlag
              disableNavigation
              showNewRegistration
            />
            <Icon
              name={!isExpanded ? IconName.ChevronDown : IconName.ChevronUp}
              className="text-primary"
              size={16}
            />
          </button>
        </td>
        <td className="text-center px-2">
          <CheckAssuranceColumn userProfile={user} />
        </td>
        <td className="text-center px-2">
          <CurrentQualificationColumn userProfile={user} />
        </td>
        <td className="text-center px-2">
          <PreviousMonthQualificationColumn userProfile={user} />
        </td>
        <td className="text-center px-2">
          <HighesthQualificationColumn userProfile={user} />
        </td>
        <td className="text-center px-2">
          <FitlineClubMemberColumn userProfile={user} />
        </td>

        <td className="text-center px-4">
          <AutoshipsColumn userProfile={user} />
        </td>

        <td className="text-right px-2">
          <PersonalSalesColumn userProfile={user} />
        </td>

        {isCurrentMonth && (
          <td className="text-right px-2">
            <OpenAutoshipVolumeColumn userProfile={user} />
          </td>
        )}

        <td className="text-right pl-4">
          <SixLevelSalesColumn userProfile={user} />
        </td>
        <td className="text-left pr-4 pl-2">
          {!_.isUndefined(sixLevelTrend) && (
            <TrendValue value={sixLevelTrend} style="illustrated" />
          )}
        </td>

        <td className="text-right pl-4">
          <StructureSalesColumn userProfile={user} />
        </td>
        <td className="text-left pr-4 pl-2">
          {!_.isUndefined(structureTrend) && (
            <TrendValue value={structureTrend} style="illustrated" />
          )}
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td
            className="transition duration-300 ease-in-out hover:bg-gray-100 bg-white overflow-visible border-y border-slate-100 p-0"
            colSpan={13}
          >
            <div
              className="w-full h-full space-y-4 sticky left-0 p-4"
              style={{ width: containerWidth }}
            >
              <div className="w-full flex items-center gap-4">
                <h2 className="flex items-center gap-1">
                  <MdOutlineHistory />
                  {t('History')}
                </h2>
                <NavLink
                  to={`/downline/pathTo?userId=${user.userId}`}
                  rel="noreferrer"
                  className="flex flex-row flex-nowrap gap-xs items-center w-fit text-primary hover:text-active"
                >
                  <span>{t('Go to dashboard')}</span>
                </NavLink>
              </div>

              <HistoryTable userId={user.userId} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
